import React, { useEffect, useState } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import type { ConsolidatedChildItem } from '../../../../../api/types/dasTickets.types';
import { consolidationService } from '../../../../../api/services/das/consolidationService';

interface TicketConsolidatedItemsProps {
  consolidatedRequestId: string;
  itemCount?: number;
  totalValue?: number;
}

const formatDate = (dateString: string | undefined): string => {
  if (!dateString) return 'N/A';
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
};

const formatValue = (value: number | undefined): string => {
  if (value === undefined || value === null) return '-';
  return new Intl.NumberFormat('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);
};

const getStatusColor = (status: string): string => {
  const statusLower = status.toLowerCase();
  if (statusLower.includes('approved') || statusLower.includes('closed')) return '#10b981';
  if (statusLower.includes('rejected')) return '#ef4444';
  if (statusLower.includes('pending') || statusLower.includes('review')) return '#f59e0b';
  if (statusLower.includes('consolidation')) return '#6366f1';
  return '#64748b';
};

export const TicketConsolidatedItems: React.FC<TicketConsolidatedItemsProps> = ({
  consolidatedRequestId,
  itemCount,
  totalValue,
}) => {
  const [items, setItems] = useState<ConsolidatedChildItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchConsolidatedItems = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await consolidationService.getChildren(consolidatedRequestId);
        // Map the response to ConsolidatedChildItem format
        const mappedItems: ConsolidatedChildItem[] = response.map((req: any) => ({
          id: req.id,
          ticketNo: req.requestNumber,
          subject: req.title,
          value: req.requestValue,
          status: req.currentStatus,
          createdByUserName: req.submittedByName || 'Unknown',
          preApprovedAt: req.preApprovedAt,
          preApprovedByName: req.preApprovedByName,
        }));
        setItems(mappedItems);
      } catch (err) {
        console.error('Failed to fetch consolidated items:', err);
        setError('Failed to load consolidated items');
      } finally {
        setLoading(false);
      }
    };

    if (consolidatedRequestId) {
      fetchConsolidatedItems();
    }
  }, [consolidatedRequestId]);

  if (loading) {
    return (
      <div style={{ padding: '3rem', textAlign: 'center' }}>
        <div style={{ fontSize: '0.875rem', color: 'var(--text-secondary)' }}>
          Loading consolidated items...
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ padding: '3rem', textAlign: 'center' }}>
        <div style={{ fontSize: '0.875rem', color: '#ef4444' }}>
          {error}
        </div>
      </div>
    );
  }

  if (items.length === 0) {
    return (
      <div style={{ padding: '3rem', textAlign: 'center' }}>
        <div style={{ fontSize: '0.875rem', color: 'var(--text-secondary)' }}>
          No consolidated items found.
        </div>
      </div>
    );
  }

  return (
    <div style={{ padding: '1.5rem' }}>
      {/* Summary Header */}
      <div style={{ marginBottom: '1.5rem' }}>
        <h3 style={{ margin: '0 0 0.5rem 0', fontSize: '1rem', fontWeight: 600, color: 'var(--text-primary)' }}>
          Consolidated Items
        </h3>
        <p style={{ margin: 0, fontSize: '0.875rem', color: 'var(--text-secondary)' }}>
          Pre-approved requests included in this consolidation
        </p>
      </div>

      {/* Summary Stats */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
          gap: '1rem',
          marginBottom: '1.5rem',
          padding: '1rem',
          background: 'var(--bg-secondary)',
          borderRadius: '0.5rem',
          border: '1px solid var(--border-color)',
        }}
      >
        <div>
          <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', marginBottom: '0.25rem' }}>
            Total Items
          </div>
          <div style={{ fontSize: '1.5rem', fontWeight: 700, color: 'var(--primary)' }}>
            {itemCount ?? items.length}
          </div>
        </div>
        <div>
          <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', marginBottom: '0.25rem' }}>
            Total Value
          </div>
          <div style={{ fontSize: '1.5rem', fontWeight: 700, color: '#10b981' }}>
            {formatValue(totalValue ?? items.reduce((sum, item) => sum + (item.value || 0), 0))}
          </div>
        </div>
      </div>

      {/* Items Table */}
      <div
        style={{
          border: '1px solid var(--border-color)',
          borderRadius: '0.5rem',
          overflow: 'hidden',
        }}
      >
        {/* Table Header */}
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: '120px 1fr 120px 120px 150px',
            gap: '1rem',
            padding: '0.75rem 1rem',
            background: 'var(--bg-secondary)',
            borderBottom: '1px solid var(--border-color)',
            fontSize: '0.75rem',
            fontWeight: 600,
            color: 'var(--text-secondary)',
            textTransform: 'uppercase',
          }}
        >
          <div>Request No.</div>
          <div>Subject</div>
          <div style={{ textAlign: 'right' }}>Value</div>
          <div>Status</div>
          <div>Pre-Approved</div>
        </div>

        {/* Table Body */}
        {items.map((item, index) => (
          <div
            key={item.id}
            style={{
              display: 'grid',
              gridTemplateColumns: '120px 1fr 120px 120px 150px',
              gap: '1rem',
              padding: '0.875rem 1rem',
              borderBottom: index < items.length - 1 ? '1px solid var(--border-color)' : 'none',
              alignItems: 'center',
              background: index % 2 === 0 ? 'transparent' : 'var(--bg-secondary)',
            }}
          >
            {/* Request Number */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <IconSystem.file size={14} style={{ color: 'var(--text-secondary)' }} />
              <span style={{ fontSize: '0.875rem', fontWeight: 500, color: 'var(--primary)' }}>
                {item.ticketNo}
              </span>
            </div>

            {/* Subject */}
            <div
              style={{
                fontSize: '0.875rem',
                color: 'var(--text-primary)',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
              title={item.subject}
            >
              {item.subject}
            </div>

            {/* Value */}
            <div style={{ textAlign: 'right', fontSize: '0.875rem', fontWeight: 500, color: 'var(--text-primary)' }}>
              {formatValue(item.value)}
            </div>

            {/* Status */}
            <div>
              <span
                style={{
                  padding: '0.25rem 0.5rem',
                  background: `${getStatusColor(item.status)}20`,
                  color: getStatusColor(item.status),
                  fontSize: '0.75rem',
                  fontWeight: 600,
                  borderRadius: '0.25rem',
                  whiteSpace: 'nowrap',
                }}
              >
                {item.status}
              </span>
            </div>

            {/* Pre-Approved Info */}
            <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>
              {item.preApprovedAt ? (
                <div>
                  <div>{formatDate(item.preApprovedAt)}</div>
                  {item.preApprovedByName && (
                    <div style={{ color: 'var(--text-muted)' }}>by {item.preApprovedByName}</div>
                  )}
                </div>
              ) : (
                '-'
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Footer Note */}
      <div
        style={{
          marginTop: '1rem',
          padding: '0.75rem',
          background: 'rgba(99, 102, 241, 0.1)',
          borderRadius: '0.375rem',
          borderLeft: '3px solid #6366f1',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <IconSystem.info size={16} style={{ color: '#6366f1' }} />
          <span style={{ fontSize: '0.8125rem', color: 'var(--text-secondary)' }}>
            These requests were pre-approved and grouped together for final approval.
          </span>
        </div>
      </div>
    </div>
  );
};

export default TicketConsolidatedItems;
